import { memo, useState, useEffect } from "react";
import StatCardRow from "../../shared/StatCardRow";
import RecordModal from "../../shared/RecordModal";
import DashboardTable from "../../shared/DashboardTable";
import Timeline from "../../shared/Timeline";
import Pager from "../../shared/Pager";
import InfoBanner from "../../shared/InfoBanner";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { useMountFlash } from "../../hooks/useMountFlash";
import { fetchCases, fetchIncidents } from "../../services/dashboardApi";
import { formatDate } from "../../utils/formatters";
import { toPlainText } from "../../utils/plainText";
import "../../reportDashboard.scss";

const reporterColumns = [
  // minWidth stops this column from being crushed below a readable width
  // when the fixed-width columns beside it (Total Cases/Open/Oldest Open/
  // urgent badge) eat most of this card's half-page width — MUI's flex
  // shrinks a column to fit its container with no floor unless one is set,
  // which was truncating even the "Reporter" header itself to "Rep…".
  { field: "reporter", headerName: "Reporter", flex: 1, minWidth: 110 },
  { field: "total", headerName: "Total Cases", width: 120, type: "number" },
  {
    field: "open",
    headerName: "Open",
    width: 90,
    type: "number",
    renderCell: (params) => (
      <span className={`rdValue is-${params.value > 0 ? "danger" : "kudos"}`}>
        {params.value}
      </span>
    ),
  },
  {
    field: "maxAge",
    headerName: "Oldest Open",
    width: 120,
    valueFormatter: ({ value }) => `${value} days`,
  },
  {
    field: "urgent",
    headerName: "",
    width: 100,
    sortable: false,
    align: "center",
    headerAlign: "center",
    valueGetter: (params) => params.row.open,
    renderCell: (params) =>
      params.row.open > 5 ? (
        <span className="rdPill rdPill--soft is-danger">URGENT</span>
      ) : null,
  },
];

const AGE_TONES = {
  "Recent (<30d)": "kudos",
  "Aging (30-90d)": "warning",
  "Old (90-180d)": "corrective",
  "Stale (180d+)": "danger",
};

const dotTone = (status) => {
  const key = (status || "").toLowerCase();
  if (key === "open") return "danger";
  if (key === "closed") return "kudos";
  if (key === "in progress") return "warning";
  return "muted";
};

const IncidentsSection = ({ data, apiCtx }) => {
  const { kpis, incidentTypes = [] } = data;
  const reporterAccountability = data.reporterAccountability || [];
  const caseAgeBuckets = data.caseAgeBuckets || [];
  const draftCounts = data.draftCounts || { incidents: 0 };
  const [detail, setDetail] = useState(null);
  const [reporterDetail, setReporterDetail] = useState(null);
  const [linkedIncident, setLinkedIncident] = useState(null);

  // Page-payload-fed tables below have no fetch of their own to report; this
  // gives them a brief loading state when the section mounts (tab switch). A
  // page-payload refetch (date-range change) is deliberately NOT included: the
  // shell shows one centred spinner for that, so a section adding its own here
  // would put a second loader on screen for a single click.
  const mountFlash = useMountFlash();
  const tableLoading = mountFlash;

  // v26 renders this as a scrolling timeline, not a paged table, but a fixed
  // 200-row page still silently dropped every case past the 200th with no
  // page controls to reach them. Now paged 25-at-a-time with a Pager under
  // the Timeline (below) so every case stays reachable.
  const {
    rows: caseRows,
    totalCount: caseTotalCount,
    page: casePage,
    setPage: setCasePage,
    pageSize: casePageSize,
    loading: casesLoading,
  } = usePaginatedList(
    (params) => fetchCases(apiCtx, params),
    [apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
    { pageSize: 25 },
  );

  useEffect(() => {
    if (!detail) {
      setLinkedIncident(null);
      return;
    }
    let alive = true;
    fetchIncidents(apiCtx, { caseId: detail.caseId, pageSize: 1 }).then(
      (result) => {
        if (alive) setLinkedIncident(result?.rows?.[0] || null);
      },
    );
    return () => {
      alive = false;
    };
  }, [detail, apiCtx]);

  // reporterCaseList replaces the old one-shot fetchCases({search, pageSize:200})
  // with a server-paged list keyed on the selected reporter, the same guard
  // pattern CredentialsSection's assignmentList already uses: fetchPage
  // resolves an empty page whenever nothing is selected, so the hook can be
  // called unconditionally instead of only mounting once a reporter is
  // clicked. openReporterDetail itself is now just a state set — the case
  // rows arrive through the hook, keyed by reporterDetail.reporter below.
  const reporterCaseList = usePaginatedList(
    (params) =>
      reporterDetail
        ? fetchCases(apiCtx, { search: reporterDetail.reporter, ...params })
        : Promise.resolve({ rows: [], totalCount: 0 }),
    [reporterDetail?.reporter, apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
  );
  const openReporterDetail = (r) => setReporterDetail(r);

  const maxBucket = Math.max(...caseAgeBuckets.map((b) => b.count), 1);

  // CasesQuery.GetCasesPaged (the endpoint fetchCases hits) orders by
  // CreatedOn descending — entry order, not DateOfEvent. That matches the
  // rest of the product: CasesListData.GetCaseList (the real, non-dashboard
  // case list feature) orders by CaseId descending, which is the same
  // creation-order convention since CaseId is an auto-increment PK. No case
  // list anywhere else in this codebase sorts by DateOfEvent, so the
  // timeline takes the API's order as-is instead of re-sorting by event date
  // — that re-sort would have made this the one case list in the product
  // that behaves differently from every other one, and it only reordered
  // within each fetched page rather than across the whole result set.
  // No IncidentType field on CaseDto itself (cases and incidents are separate
  // tables joined only by CaseId) — the timeline meta line stays date +
  // status only rather than fabricating a "Case" filler badge per row. The
  // real incident type/report link still surfaces in the case modal below.
  const timelineItems = caseRows.map((c) => ({
    id: c.caseId,
    date: formatDate(c.dateOfEvent),
    status: c.status,
    tone: dotTone(c.status),
    title: c.location || "No location recorded",
    subtitle: c.createdBy ? `Entered by ${c.createdBy}` : null,
  }));

  return (
    <div>
      {draftCounts.incidents > 0 && (
        <InfoBanner>
          <strong>{draftCounts.incidents}</strong> incident report
          {draftCounts.incidents === 1 ? "" : "s"}{" "}
          {draftCounts.incidents === 1 ? "is" : "are"} currently in Draft
          status and {draftCounts.incidents === 1 ? "has" : "have"} not been
          submitted — excluded from the totals below.
        </InfoBanner>
      )}

      <StatCardRow
        items={[
          { label: "Total Cases", value: kpis.totalCases, tone: "warning" },
          {
            label: "Open Cases",
            value: kpis.openCases,
            tone: "danger",
            badge: kpis.openCases > 0 ? "ACTION NEEDED" : null,
          },
          {
            label: "Incidents Logged",
            value: kpis.totalIncidents,
            tone: "kudos",
          },
        ]}
        columns={3}
      />

      <div className="rdGrid rdGrid--1-2">
        <div className="rdCard rdCard--flush">
          <div className="rdCard__title">Incident Overview</div>
          {incidentTypes.length > 0 ? (
            incidentTypes.map((t) => (
              <div className="rdKeyRow" key={t.incidentType}>
                <span className="rdKeyRow__label">
                  <span className="rdDot is-corrective" />
                  {toPlainText(t.incidentType)}
                </span>
                <span className="rdKeyRow__value is-muted">{t.count}</span>
              </div>
            ))
          ) : (
            <div className="rdEmpty">No incidents logged</div>
          )}
          <div className="rdKeyRow rdKeyRow--divided">
            <span className="rdKeyRow__label">Open Cases</span>
            <span className="rdKeyRow__value is-warning">{kpis.openCases}</span>
          </div>
          <div className="rdKeyRow">
            <span className="rdKeyRow__label">Closed Cases</span>
            <span className="rdKeyRow__value is-kudos">{kpis.closedCases}</span>
          </div>
        </div>

        <div className="rdCard rdCard--flush">
          <div className="rdCard__title">
            <span>Case Timeline</span>
            {timelineItems.length > 0 && (
              <span className="rdCard__note">click for details</span>
            )}
          </div>
          <Timeline
            items={timelineItems}
            emptyLabel="No cases recorded"
            onItemClick={(item) =>
              setDetail(caseRows.find((c) => c.caseId === item.id))
            }
            maxHeight={320}
          />
          <Pager
            page={casePage}
            pageSize={casePageSize}
            rowCount={caseTotalCount}
            onPageChange={setCasePage}
            loading={casesLoading}
          />
        </div>
      </div>

      {reporterAccountability.length > 0 && (
        <>
          <div className="rdSectionLabel">Case Reporter Accountability</div>
          <div className="rdGrid rdGrid--2">
            <div className="rdCard rdCard--flush">
              <div className="rdCard__title">
                <span>Open Cases by Reporter</span>
                <span className="rdCard__note">
                  click reporter for case list
                </span>
              </div>
              <DashboardTable
                columns={reporterColumns}
                rows={reporterAccountability}
                getRowId={(r) => r.reporter}
                onRowClick={(params) => openReporterDetail(params.row)}
                loading={tableLoading}
              />
            </div>
            <div className="rdCard rdCard--flush">
              <div className="rdCard__title">Open Case Age Distribution</div>
              <div className="rdCard__hint">
                How long each still-Open case (no incident forms started yet)
                has been sitting — In Progress and Closed cases are excluded,
                since neither reflects a stalled case
              </div>
              {caseAgeBuckets.map((b) => (
                <div className="rdBarGroup" key={b.bucket}>
                  <div className="rdKeyRow">
                    <span className="rdKeyRow__label">{b.bucket}</span>
                    <span
                      className={`rdKeyRow__value is-${AGE_TONES[b.bucket] || "muted"}`}
                    >
                      {b.count}
                    </span>
                  </div>
                  <div className="rdBar">
                    <div
                      className={`rdBar__fill is-${AGE_TONES[b.bucket] || "muted"}`}
                      style={{ "--rd-fill": `${(b.count / maxBucket) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
              <div className="rdNotice">
                {kpis.openCases} open cases of {kpis.totalCases} total
              </div>
            </div>
          </div>
        </>
      )}

      {detail && (
        <RecordModal
          open
          onClose={() => setDetail(null)}
          title={`Case #${detail.caseId}`}
          fields={[
            { label: "Case ID", value: detail.caseId },
            { label: "Status", value: detail.status },
            { label: "Date", value: formatDate(detail.dateOfEvent) },
            { label: "Time", value: detail.timeOfEvent },
            {
              label: "Location",
              value: detail.location || "Not recorded",
            },
            { label: "Entered By", value: detail.createdBy || "—" },
            ...(linkedIncident
              ? [
                  { label: "Report ID", value: linkedIncident.incidentId },
                  { label: "Type", value: linkedIncident.incidentType },
                ]
              : []),
          ]}
          warning={
            (detail.status || "").toLowerCase() === "open"
              ? {
                  tone: "danger",
                  text: "⚠ Open Case — This case remains unresolved. Immediate follow-up and closure documentation required.",
                }
              : null
          }
        />
      )}

      {reporterDetail && (
        <RecordModal
          open
          onClose={() => setReporterDetail(null)}
          title={reporterDetail.reporter}
          fields={[
            { label: "Total Cases", value: reporterDetail.total },
            { label: "Open Cases", value: reporterDetail.open },
            { label: "Oldest Open", value: `${reporterDetail.maxAge} days` },
          ]}
          warning={
            reporterDetail.open > 0
              ? {
                  tone: reporterDetail.open > 5 ? "danger" : "warning",
                  text: `⚠ ${reporterDetail.open} Open Case${reporterDetail.open > 1 ? "s" : ""} — These case${reporterDetail.open > 1 ? "s are" : " is"} still in Open status — general details have been recorded but no incident forms have been started yet.${reporterDetail.maxAge >= 180 ? ` The oldest has been sitting Open for ${reporterDetail.maxAge} days — well beyond the recommended 30-day window to begin incident documentation.` : ""}`,
                }
              : null
          }
          sections={[
            {
              type: "cards",
              title: "Case History",
              rows: reporterCaseList.rows.map((c) => ({
                title: `Case #${c.caseId}`,
                pill: c.status,
                // Age is only a meaningful signal for a case still sitting
                // Open — once it's In Progress or Closed there's no true
                // "date closed" field to show a real duration instead, so
                // the indicator is omitted rather than left climbing
                // forever (same rule as before, now applied inline since
                // there's no separate `history` array to pre-compute it into).
                meta:
                  (c.status || "").toLowerCase() === "open"
                    ? `${c.ageDays} days old`
                    : undefined,
                note: `${formatDate(c.dateOfEvent)} · ${c.location || "No location"}`,
              })),
              emptyLabel: "No cases found",
              rowCount: reporterCaseList.totalCount,
              page: reporterCaseList.page,
              pageSize: reporterCaseList.pageSize,
              onPageChange: reporterCaseList.setPage,
              loading: reporterCaseList.loading,
            },
          ]}
        />
      )}
    </div>
  );
};

export default memo(IncidentsSection);
